/**
 * Pure mappers translating the Create Monthly Challenge wizard's UI model into
 * the campaign API contract (FE-2321).
 *
 * The wizard speaks "Monthly Challenge" while the backend speaks "campaign";
 * this module is the single translation layer between the two. The wizard
 * follows the Season lifecycle: the campaign is created as a draft on Step 1
 * (`buildMonthlyDraftCampaignRequest`), its challenge groups map via
 * `mapMonthlyChallengeToChallengeRequest`, and the real window is written on the
 * Schedule step (`mapMonthlyScheduleToUpdateCampaignRequest`) just before the
 * campaign is transitioned out of `draft`.
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  pickStreakResetHour,
  type MonthlyChallengeDraft,
} from "../../components/GamePass/Monthly/monthlyChallengeData";
import {
  MONTHLY_SCHEDULE_INITIAL_VALUES,
  type MonthlyScheduleValues,
} from "../../components/GamePass/Monthly/monthlyScheduleData";
import { CAMPAIGN_DURATION_MODE } from "../../types/gamePass";
import type {
  CreateCampaignChallengeRequest,
  CreateCampaignRequest,
  UpdateCampaignRequest,
} from "../../types/gamePass";
import { combineDateAndTime } from "./useUpdateSeason";

dayjs.extend(utc);

// The campaign create endpoint (like Season's) requires a `start_date` /
// `end_date` with end > start, but a Step 1 draft has no real window yet — the
// admin sets that on the Schedule step. Stamp a tiny 1-minute placeholder ~1
// month out (never in the past); the admin's real dates overwrite it via
// `mapMonthlyScheduleToUpdateCampaignRequest` before the campaign is scheduled.
// Mirrors `buildTempDraftWindow` in `CreateSeason`.
const PLACEHOLDER_DRAFT_WINDOW_MINUTES = 1;

const buildDraftWindow = (): { start: string; end: string } => {
  const start = dayjs
    .utc()
    .add(1, "month")
    .startOf("month")
    .hour(9)
    .minute(0)
    .second(0)
    .millisecond(0);
  return {
    start: start.toISOString(),
    end: start.add(PLACEHOLDER_DRAFT_WINDOW_MINUTES, "minute").toISOString(),
  };
};

/**
 * Build the `POST /campaigns` body for the Step 1 draft: just the trimmed name,
 * `FIXED` duration mode, and a placeholder window (see `buildDraftWindow`). The
 * real schedule is applied later via `mapMonthlyScheduleToUpdateCampaignRequest`.
 *
 * `operator_id`, `auto_enroll`, and `reward_currency` are intentionally not set
 * here — `operator_id` is attached in `useCreateCampaign` from `GET /config`,
 * and the wizard doesn't collect the other two, so the backend default applies.
 */
export const buildMonthlyDraftCampaignRequest = (
  name: string,
): CreateCampaignRequest => {
  const window = buildDraftWindow();
  return {
    name: name.trim(),
    // TODO(FE-2257 / BE): SEASON duration mode is meant to pin the window to the
    // linked season. Until that contract is confirmed we always send FIXED with
    // an explicit start/end.
    duration_mode: CAMPAIGN_DURATION_MODE.FIXED,
    start_date: window.start,
    end_date: window.end,
  };
};

/**
 * Build the `PUT /campaigns/{id}` body from the Schedule step, overwriting the
 * Step 1 placeholder window with the admin's real dates just before the
 * campaign is scheduled.
 *
 * The schedule pickers hand back a local calendar day + wall-clock time; the
 * shared `combineDateAndTime` helper composes and converts them to a UTC ISO
 * instant, matching how Seasons serialise their window. Dates are only set when
 * both the date and time are present and valid, so a half-filled schedule omits
 * them rather than sending an invalid string.
 *
 * Chaining after a season also flips `duration_mode` from the draft's `FIXED` to
 * `SEASON`: `linked_season_id` is the SEASON-mode field (a `FIXED` campaign has
 * none), so leaving the mode `FIXED` would send an inconsistent record the
 * backend ignores at scheduling time. The window is still sent — the monthly
 * Schedule step authors an explicit end even while auto-start fills the start —
 * for the backend to reconcile against the linked season (PROVISIONAL, pending
 * BE confirmation of how SEASON mode treats an explicit end).
 */
export const mapMonthlyScheduleToUpdateCampaignRequest = (
  schedule: MonthlyScheduleValues,
): UpdateCampaignRequest => {
  const request: UpdateCampaignRequest = {};

  const startDate = combineDateAndTime(schedule.startDate, schedule.startTime);
  if (startDate) {
    request.start_date = startDate;
  }

  const endDate = combineDateAndTime(schedule.endDate, schedule.endTime);
  if (endDate) {
    request.end_date = endDate;
  }

  if (schedule.autoStartAfter && schedule.previousSeasonId) {
    request.linked_season_id = schedule.previousSeasonId;
    request.duration_mode = CAMPAIGN_DURATION_MODE.SEASON;
  }

  return request;
};

/**
 * Campaign patch for `streak_reset_hour`. A Login Streak card with a valid UTC
 * hour (0–23) stamps that hour; otherwise the field is explicitly nulled so a
 * prior Login Streak save cannot leave a stale campaign hour after the task
 * type changes.
 */
export const buildStreakResetHourPatch = (
  challenges: ReadonlyArray<Partial<MonthlyChallengeDraft>>,
): Pick<UpdateCampaignRequest, "streak_reset_hour"> => {
  const hour = pickStreakResetHour(challenges);
  return { streak_reset_hour: hour === undefined ? null : hour };
};

/**
 * Reverse of {@link mapMonthlyScheduleToUpdateCampaignRequest} for the Details
 * edit modal: split a campaign's stored UTC window back into the schedule
 * pickers' local date + time `Dayjs` pair (the pickers read the calendar day
 * off `*Date` and the wall-clock off `*Time`, so both share the same local
 * instant). Returns the empty INITIAL values when the window is missing or
 * unparseable — e.g. the mock deep-link records carry no ISO dates — so the
 * modal renders blank pickers rather than an "Invalid Date". `autoStartAfter`
 * is always seeded off so re-opening the modal never re-locks the start; the
 * admin re-toggles auto-start explicitly if they want it.
 */
export const mapCampaignWindowToMonthlySchedule = (
  start?: string | null,
  end?: string | null,
): MonthlyScheduleValues => {
  const startInstant = start ? dayjs.utc(start).local() : null;
  const endInstant = end ? dayjs.utc(end).local() : null;
  const startValid =
    startInstant && startInstant.isValid() ? startInstant : undefined;
  const endValid = endInstant && endInstant.isValid() ? endInstant : undefined;

  if (!startValid && !endValid) {
    return { ...MONTHLY_SCHEDULE_INITIAL_VALUES };
  }

  return {
    autoStartAfter: false,
    startDate: startValid,
    startTime: startValid ?? MONTHLY_SCHEDULE_INITIAL_VALUES.startTime,
    endDate: endValid,
    endTime: endValid ?? MONTHLY_SCHEDULE_INITIAL_VALUES.endTime,
  };
};

/**
 * Map one wizard challenge card into a challenge-group create request under the
 * given campaign. `sortOrder` is the group's zero-based position, stamped by
 * the caller from the card's index. `description` is only included when the
 * admin entered one, matching how Season challenges are serialised.
 *
 * The card's task type, tiers, and pick/casino gating are NOT mapped here —
 * those become the group's tiered task in the follow-up task mapper.
 */
export const mapMonthlyChallengeToChallengeRequest = (
  challenge: MonthlyChallengeDraft,
  campaignId: string,
  sortOrder: number,
): CreateCampaignChallengeRequest => {
  const request: CreateCampaignChallengeRequest = {
    campaign_id: campaignId,
    name: challenge.name.trim(),
    sort_order: sortOrder,
  };

  const description = challenge.description.trim();
  if (description) {
    request.description = description;
  }

  return request;
};
